import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

import AccountsList from "./accounting/AccountsList";
import JournalEntries from "./accounting/JournalEntries";
import FinancialStatements from "./accounting/FinancialStatements";

/**
 * AccountingDashboard — thin shell that loads data and switches between
 * Overview / Accounts / Journal / Statements tabs. All UI lives in
 * `/accounting/*` child components.
 */
const AccountingDashboard = () => {
  const { user } = useAuth();
  const isDataEntry = user?.role === "data_entry";

  const [activeTab, setActiveTab] = useState(isDataEntry ? "accounts" : "overview");
  const [accounts, setAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [statements, setStatements] = useState({
    trialBalance: null,
    incomeStatement: null,
    balanceSheet: null,
    cashFlow: null,
    equity: null,
  });
  const [loading, setLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounting/accounts`);
      setAccounts(response.data.accounts || []);
    } catch (error) {
      if (error.response?.status === 403) toast.error("Access denied");
    }
  }, []);

  const fetchJournalEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounting/journal-entries?limit=20`);
      setJournalEntries(response.data.entries || []);
    } catch (error) {
      /* non-critical */
    }
  }, []);

  const fetchStatements = useCallback(async () => {
    try {
      const [tb, is, bs, cf, eq] = await Promise.all([
        axios.get(`${API}/accounting/statements/trial-balance`),
        axios.get(`${API}/accounting/statements/income-statement`),
        axios.get(`${API}/accounting/statements/balance-sheet`),
        axios.get(`${API}/accounting/statements/cash-flow`),
        axios.get(`${API}/accounting/statements/equity`),
      ]);
      setStatements({
        trialBalance: tb.data,
        incomeStatement: is.data,
        balanceSheet: bs.data,
        cashFlow: cf.data,
        equity: eq.data,
      });
    } catch (error) {
      /* non-critical */
    }
  }, []);

  const initializeAccounts = async () => {
    try {
      await axios.post(`${API}/accounting/accounts/initialize`);
      toast.success("Chart of Accounts initialized");
      fetchAccounts();
      if (!isDataEntry) fetchStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const tasks = [fetchAccounts(), fetchJournalEntries()];
      if (!isDataEntry) tasks.push(fetchStatements());
      await Promise.all(tasks);
      setLoading(false);
    };
    loadData();
  }, [fetchAccounts, fetchJournalEntries, fetchStatements, isDataEntry]);

  const refreshAll = useCallback(() => {
    fetchAccounts();
    fetchJournalEntries();
    if (!isDataEntry) fetchStatements();
  }, [fetchAccounts, fetchJournalEntries, fetchStatements, isDataEntry]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const tabs = isDataEntry ? ["accounts", "journal"] : ["overview", "accounts", "journal", "statements"];

  return (
    <div className="space-y-6" data-testid="accounting-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Accounting</h1>
          <p className="text-muted-foreground">Chart of Accounts &amp; Financial Statements</p>
        </div>
        {accounts.length === 0 && !isDataEntry && (
          <Button onClick={initializeAccounts} variant="outline" data-testid="initialize-accounts-btn">
            Initialize Accounts
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-md font-medium text-sm whitespace-nowrap ${
              activeTab === tab ? "bg-primary text-white" : "text-muted-foreground hover:bg-gray-100"
            }`}
            data-testid={`accounting-tab-${tab}`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === "overview" && !isDataEntry && (
        <FinancialStatements statements={statements} journalEntries={journalEntries} mode="overview" />
      )}
      {activeTab === "accounts" && (
        <AccountsList accounts={accounts} isDataEntry={isDataEntry} onRefresh={fetchAccounts} />
      )}
      {activeTab === "journal" && (
        <JournalEntries accounts={accounts} journalEntries={journalEntries} onRefresh={refreshAll} />
      )}
      {activeTab === "statements" && !isDataEntry && (
        <FinancialStatements statements={statements} journalEntries={journalEntries} mode="statements" />
      )}
    </div>
  );
};

export default AccountingDashboard;
